import React from 'react';
import { ExtractionQueueData } from '../useAdminQueues';

interface ExtractionQueueBlockProps {
  extractionQueue: ExtractionQueueData | null;
  onToggle: () => void;
}

const formatEta = (minutes: number): string => {
  if (minutes < 1) return '< 1 min';
  if (minutes >= 60) return `${Math.round(minutes / 60)}h ${Math.round(minutes % 60)}m`;
  return `${Math.round(minutes)} min`;
};

/** Superadmin: content-extraction queue with ambient progress + transcription. */
const ExtractionQueueBlock: React.FC<ExtractionQueueBlockProps> = ({
  extractionQueue,
  onToggle,
}) => {
  if (!extractionQueue) return null;
  const { queue, scheduler, stats, estimate, transcription } = extractionQueue;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="tend-micro">Content Extraction</span>
        <span
          title={
            scheduler.paused
              ? 'Paused'
              : queue.pending_total > 0
              ? 'Processing articles'
              : 'Idle — queue empty'
          }
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: scheduler.paused
              ? 'var(--signal-rec-soft)'
              : queue.pending_total > 0
              ? 'var(--signal-live)'
              : 'rgba(255,255,255,.3)',
            boxShadow:
              !scheduler.paused && queue.pending_total > 0
                ? '0 0 8px rgba(68,211,106,.7)'
                : 'none',
          }}
        />
        <button
          type="button"
          className="tend-btn tend-btn--glass"
          style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: 12 }}
          onClick={onToggle}
        >
          {scheduler.paused ? 'Resume' : 'Pause'}
        </button>
      </div>

      {scheduler.paused && (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--signal-rec-soft)' }}>
          Extraction paused — new items will queue but not process.
        </p>
      )}

      {queue.pending_total > 0 ? (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="tend-progress" style={{ flex: 1 }}>
              <i style={{ width: `${queue.percent_complete}%` }} />
            </div>
            <span className="tend-micro">{queue.percent_complete}%</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-3)' }}>
            <span>
              <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>{queue.pending_total}</span>{' '}
              pending
            </span>
            <span>
              {queue.total_extracted} / {queue.total_articles} articles
            </span>
          </div>
          {estimate.estimated_minutes != null && (
            <p style={{ margin: 0, fontSize: 12, color: 'var(--text-3)' }}>
              Est. {formatEta(estimate.estimated_minutes)} remaining
              {stats.avg_seconds_per_item != null && ` (${stats.avg_seconds_per_item}s/item)`}
            </p>
          )}
          {stats.total_failed > 0 && (
            <p style={{ margin: 0, fontSize: 12, color: 'var(--signal-rec-soft)' }}>
              {stats.total_failed} failed
            </p>
          )}
        </div>
      ) : (
        <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-3)' }}>
          All {queue.total_articles} articles extracted
          {stats.total_processed > 0 && ` (${stats.total_processed} this session)`}
        </p>
      )}

      {transcription && scheduler.transcription_enabled && (
        <p
          style={{
            margin: '8px 0 0',
            paddingTop: 8,
            borderTop: '1px solid rgba(255,255,255,.10)',
            fontSize: 12,
            color: 'var(--text-3)',
          }}
        >
          🎙️{' '}
          {transcription.pending_av > 0 ? (
            <>
              <span style={{ color: 'var(--text-2)', fontWeight: 500 }}>
                {transcription.pending_av}
              </span>{' '}
              transcription{transcription.pending_av !== 1 ? 's' : ''} pending ·{' '}
              {transcription.av_transcribed} done
            </>
          ) : (
            <>{transcription.av_transcribed} transcribed</>
          )}
          {transcription.av_failed > 0 && (
            <span style={{ color: 'var(--signal-rec-soft)' }}> · {transcription.av_failed} failed</span>
          )}
        </p>
      )}
    </div>
  );
};

export default ExtractionQueueBlock;
